import type { ManagedQrCode } from './types';

export interface QrPrintLayout {
  label: string;
  codesPerSheet: number;
  columns: number;
  rows: number;
}

interface BuildPrintHtmlInput {
  codes: ManagedQrCode[];
  logoUrl?: string;
  qrImageUrl: (payload: string, size?: number) => string;
  layout?: QrPrintLayout;
}

const A4_PRINT_WIDTH_MM = 210 - 16;
const A4_PRINT_HEIGHT_MM = 297 - 18;
const A4_GRID_GAP_MM = 3;
const A4_CELL_PADDING_MM = 2.4;
const A4_CELL_BORDER_MM = 0.3;

export function toPngFileName(code: ManagedQrCode): string {
  return `${code.payload}.png`;
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildPrintReadyScript(): string {
  return `
        window.onload = function () {
          setTimeout(function () { window.print(); }, 500);
        };
  `;
}

export function truncateText(
  context: CanvasRenderingContext2D,
  value: string,
  maxWidth: number,
): string {
  if (context.measureText(value).width <= maxWidth) {
    return value;
  }

  const suffix = '...';
  let end = value.length;
  while (end > 1) {
    const candidate = `${value.slice(0, end)}${suffix}`;
    if (context.measureText(candidate).width <= maxWidth) {
      return candidate;
    }
    end -= 1;
  }
  return suffix;
}

function chunkCodes(codes: ManagedQrCode[], size: number): ManagedQrCode[][] {
  const chunks: ManagedQrCode[][] = [];
  for (let index = 0; index < codes.length; index += size) {
    chunks.push(codes.slice(index, index + size));
  }
  return chunks;
}

function calculateQrBoxSizeMm(layout: QrPrintLayout): string {
  const columnGaps = A4_GRID_GAP_MM * (layout.columns - 1);
  const rowGaps = A4_GRID_GAP_MM * (layout.rows - 1);
  const cellWidth = (A4_PRINT_WIDTH_MM - columnGaps) / layout.columns;
  const cellHeight = (A4_PRINT_HEIGHT_MM - rowGaps) / layout.rows;
  const cellChrome = A4_CELL_PADDING_MM * 2 + A4_CELL_BORDER_MM * 2;
  const size = Math.max(1, Math.min(cellWidth, cellHeight) - cellChrome);

  return `${size.toFixed(1)}mm`;
}

function buildA4GridPrintHtml({
  codes,
  layout,
  qrImageUrl,
}: {
  codes: ManagedQrCode[];
  layout: QrPrintLayout;
  qrImageUrl: (payload: string, size?: number) => string;
}): string {
  const sheets = chunkCodes(codes, layout.codesPerSheet);
  const qrBoxSize = calculateQrBoxSizeMm(layout);

  const sheetsHtml = sheets
    .map((sheetCodes) => {
      const cellsHtml = sheetCodes
        .map(
          (code) => `
            <article class='cell'>
              <div class='qr-box' style='width: ${qrBoxSize}; height: ${qrBoxSize};'>
                <img class='qr-image' src='${qrImageUrl(code.payload, 760)}' alt='${escapeHtml(code.payload)}' />
              </div>
            </article>
          `,
        )
        .join('');

      return `
        <section class='sheet' style='grid-template-columns: repeat(${layout.columns}, minmax(0, 1fr)); grid-template-rows: repeat(${layout.rows}, minmax(0, 1fr));'>
          ${cellsHtml}
        </section>
      `;
    })
    .join('');

  return `
    <!doctype html>
    <html lang='ru'>
    <head>
      <meta charset='utf-8' />
      <title>QR Export ${escapeHtml(layout.label)}</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        html, body { margin: 0; padding: 0; }
        body { background: #ffffff; font-family: Arial, sans-serif; }
        .sheet {
          width: 100%;
          height: calc(297mm - 18mm);
          box-sizing: border-box;
          display: grid;
          gap: 3mm;
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          break-inside: avoid-page;
          overflow: hidden;
        }
        .sheet:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        .cell {
          border: 0.3mm solid #ced7e3;
          border-radius: 2mm;
          display: grid;
          place-items: center;
          padding: 2.4mm;
          box-sizing: border-box;
          overflow: hidden;
          background: #ffffff;
          page-break-inside: avoid;
          break-inside: avoid;
          min-width: 0;
          min-height: 0;
        }
        .qr-box {
          max-width: 100%;
          max-height: 100%;
        }
        .qr-image {
          display: block;
          width: 100%;
          height: auto;
        }
      </style>
    </head>
    <body>
      ${sheetsHtml}
      <script>
${buildPrintReadyScript()}
      </script>
    </body>
    </html>
  `;
}

function buildLegacyPrintHtml({
  codes,
  logoUrl,
  qrImageUrl,
}: {
  codes: ManagedQrCode[];
  logoUrl: string;
  qrImageUrl: (payload: string, size?: number) => string;
}): string {
  const escapedLogoUrl = escapeHtml(logoUrl);
  const cardsHtml = codes
    .map((code) => {
      const entityText = code.assignedEntityId
        ? `привязка: ${code.assignedEntityId}`
        : 'привязка: свободен';
      return `
        <article class='card'>
          <div class='qr-wrap'>
            <img class='qr-image' src='${qrImageUrl(code.payload, 420)}' alt='${escapeHtml(code.payload)}' />
            <span class='qr-logo-badge'>
              <img src='${escapedLogoUrl}' alt='' />
            </span>
          </div>
          <h4>${escapeHtml(code.payload)}</h4>
          <p class='id'>id: ${escapeHtml(code.id)}</p>
          <p>${escapeHtml(entityText)}</p>
        </article>
      `;
    })
    .join('');

  return `
    <!doctype html>
    <html lang='ru'>
    <head>
      <meta charset='utf-8' />
      <title>QR Export</title>
      <style>
        body { margin: 0; font-family: Arial, sans-serif; padding: 14px; background: #ffffff; }
        .grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 12px; }
        .card { break-inside: avoid; border: 1px solid #d0d5dd; border-radius: 10px; padding: 10px; text-align: center; }
        .qr-wrap { position: relative; width: 100%; max-width: 220px; margin: 0 auto 8px; }
        .qr-image { width: 100%; display: block; border-radius: 8px; border: 1px solid #eaecf0; }
        .qr-logo-badge { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 42px; height: 42px; border-radius: 50%; border: 1px solid rgba(15,76,129,0.25); background: #ffffff; display: inline-flex; align-items: center; justify-content: center; }
        .qr-logo-badge img { width: 78%; height: 78%; object-fit: contain; border-radius: 50%; }
        .card h4 { margin: 0 0 4px; font-size: 12px; overflow-wrap: anywhere; }
        .card p { margin: 0; color: #475467; font-size: 11px; overflow-wrap: anywhere; line-height: 1.35; }
        .card p.id { color: #101828; margin-bottom: 2px; }
        @media print {
          body { padding: 8px; }
          .grid { gap: 8px; }
        }
      </style>
    </head>
    <body>
      <div class='grid'>${cardsHtml}</div>
      <script>
${buildPrintReadyScript()}
      </script>
    </body>
    </html>
  `;
}

export function buildQrPrintHtml(input: BuildPrintHtmlInput): string {
  if (input.layout) {
    return buildA4GridPrintHtml({
      codes: input.codes,
      layout: input.layout,
      qrImageUrl: input.qrImageUrl,
    });
  }

  return buildLegacyPrintHtml({
    codes: input.codes,
    logoUrl: input.logoUrl ?? '',
    qrImageUrl: input.qrImageUrl,
  });
}
